import { useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NativeSelect, SegmentedControl, TextInput } from "@mantine/core";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { Campaign, EnrichmentGapCount, EnrichmentOwner, EnrichmentPerson, EnrichmentProperty, Job } from "../api/types";
import { absent, orAbsent, parseLakeDate, recentDate } from "../lib/format";
import { PAGE_SIZE, applySorts, useListParams } from "../lib/useListParams";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { Toolbar } from "../ui/Toolbar";
import { RecordLink } from "./RecordLink";
import { Spinner } from "./Spinner";

/* The records that need enrichment, at the foot of a campaign's Enrichment
 * tab: the people to call, the properties and the property owners that the
 * newest measurement found missing something, one row each, with what is
 * missing in words.
 *
 * Where: James, 2026-10-19, asked for a simple area where Eula does her work,
 * with things where they make sense. She works a request from this tab, so
 * the records are here, under the tools, and each count in "Areas for further
 * enrichment" opens them filtered to that gap. docs/plans/enrichment-records.md
 * has the alternatives.
 *
 * What is missing is the engine's: the measurement's own per-record result
 * (lake.campaign_gap, engine migration 251), so the table and the counts above
 * it are one measurement. A record's details are read live, so what Eula adds
 * shows at once, and its gaps clear at the next measurement, which Measure
 * again starts.
 *
 * One Edit per row (James, 2026-10-19), through api.edit_record, the call the
 * record page's Edit makes, so the authority and grace rules apply. It changes
 * a title, adds a mobile, email or phone, sets a property's units or an
 * owner's website. Anything more, such as marking a number wrong, is on the
 * record's own page, which the name opens. A title is offered only when the
 * job on the row is the job api.edit_record corrects (title_editable, engine
 * migration 252), because for a person with two jobs it could land on the
 * other one. */

type Kind = "person" | "property" | "company";

const KINDS: { kind: Kind; label: string; view: string; search: string; noun: string }[] = [
  { kind: "person", label: "People", view: "enrichment_person", search: "Search by name", noun: "people" },
  { kind: "property", label: "Properties", view: "enrichment_property", search: "Search by name or city", noun: "properties" },
  // "Property owners", because a campaign has an owner of its own (engine
  // migration 214), as the Areas table above says it.
  { kind: "company", label: "Property owners", view: "enrichment_owner", search: "Search by name", noun: "property owners" },
];

const SELECT: Record<Kind, string> = {
  person: "person_id,full_name,title,title_editable,employment_id,employer_id,employer_name,property_id,property_name,property_address,mobile,email,phone,missing,measured_at,walk_order",
  property: "property_id,property_name,address_line1,city,state,unit_count,stage_word,missing,measured_at,walk_order",
  company: "company_id,company_name,website,hq_city,hq_state,property_id,property_name,property_address,missing,measured_at,walk_order",
};

type Row = EnrichmentPerson & EnrichmentProperty & EnrichmentOwner;

/* The time beside the day, because Measure again can measure a campaign more
   than once a day and Eula needs to know the gaps are the ones after her
   edits. On parseLakeDate, as recentDate is, so the day and the time agree. */
const timeOf = (iso?: string | null) => {
  const d = iso ? parseLakeDate(iso) : null;
  return d ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : null;
};

const keyOf = (kind: Kind, r: Row) => (kind === "person" ? r.person_id : kind === "property" ? r.property_id : r.company_id) ?? "";
const missingWords = (r: Row) => (r.missing ?? []).join(", ");

export function EnrichmentRecords({ token, c }: { token: string; c: Campaign }) {
  const name = c.name ?? "";
  const queryClient = useQueryClient();
  const [search] = useSearchParams();
  const kind = KINDS.find((k) => k.kind === search.get("r.kind")) ?? KINDS[0]!;
  const params = useListParams("walk_order", ["kind", "gap"], true, kind.view, "r.");
  const gap = params.filters.gap ?? "";
  const [editing, setEditing] = useState<string | null>(null);
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [measureJob, setMeasureJob] = useState<string | null>(null);

  const counts = useQuery({
    queryKey: ["enrichment-gap-count", name],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("enrichment_gap_count")
        .select("entity_type,gap,label,records").eq("campaign", name).limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as EnrichmentGapCount[];
    },
  });
  const all = counts.data ?? [];
  const totalOf = (k: Kind) => all.find((g) => g.entity_type === k && !g.gap)?.records ?? 0;
  const gapsOf = (k: Kind) => all.filter((g) => g.entity_type === k && g.gap)
    .sort((a, b) => (b.records ?? 0) - (a.records ?? 0));
  const listed = KINDS.reduce((n, k) => n + totalOf(k.kind), 0);

  const narrowed = (opts?: { count: "exact"; head?: boolean }) => {
    let q = apiClient(token).from(kind.view).select(SELECT[kind.kind], opts).eq("campaign", name);
    if (gap) q = q.contains("reasons", [gap]);
    const s = params.search.trim().replace(/[(),*"\\]/g, " ").trim();
    if (s) {
      q = kind.kind === "person" ? q.ilike("full_name", `*${s}*`)
        : kind.kind === "property" ? q.or(`property_name.ilike.*${s}*,address_line1.ilike.*${s}*,city.ilike.*${s}*`)
        : q.ilike("company_name", `*${s}*`);
    }
    return q;
  };
  const rows = useQuery({
    queryKey: ["enrichment-records", name, kind.kind, gap, params.search, params.sorts, params.page],
    /* The rows on screen stay while the next page or filter loads, but not
       across a change of kind. A person row keyed as a property collided
       with the other people at that property, and React kept some of them
       on screen under the property columns after the properties arrived
       (read in Chrome on NJ Test, 2026-10-19). */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === kind.kind ? keepPreviousData(previous) : undefined,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error } = await applySorts(narrowed(), params.sorts)
        .order("walk_order", { ascending: true }).range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as unknown as Row[];
    },
  });
  // The count comes from the measurement's own counts, except under a
  // search, which only the server can count.
  const searched = useQuery({
    queryKey: ["enrichment-records", "count", name, kind.kind, gap, params.search],
    enabled: Boolean(params.search.trim()),
    queryFn: async () => {
      const { error, count } = await narrowed({ count: "exact", head: true });
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? 0;
    },
  });
  const total = params.search.trim() ? (searched.data ?? null)
    : gap ? (gapsOf(kind.kind).find((g) => g.gap === gap)?.records ?? 0) : totalOf(kind.kind);
  const lastPage = total === null ? params.page : Math.max(1, Math.ceil(total / PAGE_SIZE));

  /* Measure again: one measurement, measure only, so it moves no stage and
     queues no lookups (api.measure_campaign, engine migration 251). The page
     watches the job and reads the records again when it is done. */
  const measure = useMutation({
    mutationFn: () => callFunction<{ job_id: string; status: string }>(token, "measure_campaign", { p_name: name }),
    onSuccess: (r) => setMeasureJob(r.job_id),
  });
  const job = useQuery({
    queryKey: ["measure-job", measureJob],
    enabled: Boolean(measureJob),
    refetchInterval: (q) => {
      const s = (q.state.data as Job | null | undefined)?.status;
      return s === "done" || s === "failed" ? false : 3_000;
    },
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("job")
        .select("id,status,error,finished_at").eq("id", measureJob as string).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as Job | null;
    },
  });
  const jobStatus = job.data?.status;
  useEffect(() => {
    if (jobStatus !== "done") return;
    for (const key of [["enrichment-gap-count", name], ["enrichment-records", name], ["campaign", name], ["campaign-progress", name]]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
    setSaved(new Set());
    setMeasureJob(null);
  }, [jobStatus, name, queryClient]);
  const measuring = measure.isPending || (Boolean(measureJob) && jobStatus !== "done" && jobStatus !== "failed");
  const measuredAt = rows.data?.[0]?.measured_at ?? c.measured_at;

  const setKind = (k: Kind) => {
    setEditing(null);
    params.set({ filters: { kind: k === "person" ? "" : k }, search: "", sorts: [{ column: "walk_order", ascending: true }] });
  };

  const columns = columnsFor(kind.kind);
  const editButton = (r: Row) => {
    const key = keyOf(kind.kind, r);
    return (
      <span className="er-edit">
        <Button size="compact-sm" aria-expanded={editing === key}
                onClick={() => setEditing(editing === key ? null : key)}>Edit</Button>
        {saved.has(key) ? <span className="muted" role="status">Saved</span> : null}
      </span>
    );
  };

  const needsWork = (c.contacts_need_work ?? 0) + (c.properties_need_work ?? 0) + (c.owners_need_work ?? 0);

  return (
    <div className="ce-block er" id="records">
      <h3>
        Records that need enrichment
        {measuredAt ? <span className="muted ce-when">Measured {recentDate(measuredAt)}{timeOf(measuredAt) ? `, ${timeOf(measuredAt)}` : ""}</span> : null}
      </h3>
      <p className="er-measure">
        {measuring ? <span className="muted" role="status"><Spinner />Measuring.</span>
          : <Button kind="link" size="compact-sm" onClick={() => measure.mutate()}>Measure again</Button>}
        {measure.isError ? <span className="refusal" role="alert"> {(measure.error as Error).message}</span> : null}
        {job.data?.status === "failed" ? <span className="refusal" role="alert"> The measurement failed. {job.data.error ?? ""}</span> : null}
      </p>

      {counts.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : counts.isError ? <p className="refusal" role="alert">{(counts.error as Error).message}</p>
        : !c.measured_at ? <p className="muted">Not measured yet.</p>
        : listed === 0 && needsWork > 0 ? <p className="muted">The records are listed from the next measurement. Measure again to list them now.</p>
        : listed === 0 ? <p className="muted">Every record has what enrichment looks for.</p>
        : (
          <>
            <div className="er-controls">
              <SegmentedControl
                value={kind.kind} aria-label="Which records"
                data={KINDS.map((k) => ({ value: k.kind, label: `${k.label} ${totalOf(k.kind).toLocaleString()}` }))}
                onChange={(v) => setKind(v as Kind)}
              />
              <NativeSelect
                label="Missing" value={gap} className="er-gap"
                data={[{ value: "", label: `Anything (${totalOf(kind.kind).toLocaleString()})` },
                       ...gapsOf(kind.kind).map((g) => ({ value: g.gap ?? "", label: `${g.label ?? g.gap} (${(g.records ?? 0).toLocaleString()})` }))]}
                onChange={(e) => { setEditing(null); params.set({ filters: { ...params.filters, gap: e.currentTarget.value } }); }}
              />
            </div>
            <Toolbar
              id="er"
              search={{ label: kind.search, value: params.search, onSubmit: (s) => params.set({ search: s }) }}
              onClearAll={() => params.set({ search: "", filters: { kind: params.filters.kind ?? "" } })}
              chips={gap ? [{ label: `Missing: ${gapsOf(kind.kind).find((g) => g.gap === gap)?.label ?? gap}`,
                              onRemove: () => params.set({ filters: { ...params.filters, gap: "" } }) }] : []}
            />
            {rows.isError ? (
              <p className="refusal" role="alert">{(rows.error as Error).message} <Button size="compact-sm" onClick={() => rows.refetch()}>Try again</Button></p>
            ) : rows.isLoading ? <p className="muted"><Spinner />Loading.</p>
              : !(rows.data ?? []).length ? (
                <p className="muted">
                  No {kind.noun} match{params.search ? ` "${params.search}"` : ""}.{" "}
                  <Button kind="link" size="compact-sm" onClick={() => params.set({ search: "", filters: { kind: params.filters.kind ?? "" } })}>Clear</Button>
                </p>
              ) : (
                <>
                  <p className="muted er-count">
                    {total === null ? <Spinner /> : `${total.toLocaleString()} ${kind.noun}.`}
                    {rows.isFetching ? <> <Spinner /></> : null}
                  </p>
                  <DataTable
                    columns={columns}
                    rows={rows.data ?? []}
                    rowKey={(r) => `${kind.kind}:${keyOf(kind.kind, r)}`}
                    params={params}
                    rowMenu={editButton}
                    rowExtra={(r) => editing === keyOf(kind.kind, r) ? (
                      <FixForm token={token} kind={kind.kind} row={r}
                               onCancel={() => setEditing(null)}
                               onSaved={() => {
                                 const key = keyOf(kind.kind, r);
                                 setEditing(null);
                                 setSaved((s) => new Set(s).add(key));
                                 void queryClient.invalidateQueries({ queryKey: ["enrichment-records", name] });
                               }} />
                    ) : null}
                  />
                  {lastPage > 1 ? (
                    <div className="bar cl-pager">
                      <Button onClick={() => params.set({ page: params.page - 1 })} disabled={params.page <= 1}>Previous</Button>
                      <span className="muted">Page {params.page.toLocaleString()} of {lastPage.toLocaleString()}</span>
                      <Button onClick={() => params.set({ page: params.page + 1 })} disabled={params.page >= lastPage}>Next</Button>
                    </div>
                  ) : null}
                </>
              )}
          </>
        )}
    </div>
  );
}

function columnsFor(kind: Kind): ColumnSpec<Row>[] {
  const missing: ColumnSpec<Row> = { id: "missing", header: "Missing", role: "secondary", fields: [], narrowLabel: null,
    cell: (r): ReactNode => missingWords(r) || absent() };
  if (kind === "person") {
    return [
      { id: "name", header: "Name", role: "leading", fields: [], sort: "full_name", title: true, narrowLabel: null,
        cell: (r) => <RecordLink to="people" id={r.person_id} label={r.full_name || "No name"} /> },
      { id: "title", header: "Title", role: "secondary", fields: [], narrowLabel: null, cell: (r) => orAbsent(r.title) },
      { id: "employer", header: "Employer", role: "secondary", fields: [], narrowLabel: null,
        cell: (r) => <RecordLink to="companies" id={r.employer_id} label={r.employer_name} /> },
      { id: "property", header: "Property", role: "detail", fields: [], narrowLabel: null,
        cell: (r) => <RecordLink to="properties" id={r.property_id} label={r.property_id ? (r.property_name || r.property_address || "No name or street") : null} /> },
      { id: "mobile", header: "Mobile", role: "secondary", fields: [], narrowLabel: "mobile", cell: (r) => orAbsent(r.mobile) },
      { id: "email", header: "Email", role: "secondary", fields: [], narrowLabel: "email", cell: (r) => orAbsent(r.email) },
      { id: "phone", header: "Phone", role: "detail", fields: [], narrowLabel: "phone", cell: (r) => orAbsent(r.phone) },
      missing,
    ];
  }
  if (kind === "property") {
    return [
      { id: "name", header: "Property", role: "leading", fields: [], sort: "property_name", title: true, narrowLabel: null,
        cell: (r) => <RecordLink to="properties" id={r.property_id} label={r.property_name || r.address_line1 || "No name or street"} /> },
      { id: "city", header: "City", role: "secondary", fields: [], narrowLabel: null, cell: (r) => orAbsent(r.city) },
      { id: "state", header: "State", role: "secondary", fields: [], narrowLabel: null, cell: (r) => orAbsent(r.state) },
      { id: "units", header: "Units", role: "secondary", fields: [], align: "num", narrowLabel: "units",
        cell: (r) => r.unit_count?.toLocaleString() ?? absent() },
      { id: "stage", header: "Stage", role: "detail", fields: [], narrowLabel: null, cell: (r) => orAbsent(r.stage_word) },
      missing,
    ];
  }
  return [
    { id: "name", header: "Owner", role: "leading", fields: [], sort: "company_name", title: true, narrowLabel: null,
      cell: (r) => <RecordLink to="companies" id={r.company_id} label={r.company_name || "No name"} /> },
    { id: "website", header: "Website", role: "secondary", fields: [], narrowLabel: null, cell: (r) => orAbsent(r.website) },
    { id: "city", header: "City", role: "secondary", fields: [], narrowLabel: null, cell: (r) => orAbsent(r.hq_city) },
    { id: "state", header: "State", role: "secondary", fields: [], narrowLabel: null, cell: (r) => orAbsent(r.hq_state) },
    { id: "property", header: "Property", role: "detail", fields: [], narrowLabel: null,
      cell: (r) => <RecordLink to="properties" id={r.property_id} label={r.property_id ? (r.property_name || r.property_address || "No name or street") : null} /> },
    missing,
  ];
}

/* The one Edit a row opens, under the row. Only what changed is sent, in one
   api.edit_record call. A person's title goes to their current job, a mobile,
   email or phone is added beside what is there, a property's units and an
   owner's website replace what is there. The note says where it came from,
   and is kept with the edit as its source. */
function FixForm({ token, kind, row, onCancel, onSaved }: {
  token: string; kind: Kind; row: Row; onCancel: () => void; onSaved: () => void;
}) {
  const [title, setTitle] = useState(row.title ?? "");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [units, setUnits] = useState(row.unit_count === null || row.unit_count === undefined ? "" : String(row.unit_count));
  const [website, setWebsite] = useState(row.website ?? "");
  const [note, setNote] = useState("");

  const changes = (): { type: string; id: string; changes: Record<string, unknown> } | null => {
    if (kind === "person") {
      const add = [{ kind: "mobile", value: mobile }, { kind: "email", value: email }, { kind: "phone", value: phone }]
        .map((a) => ({ ...a, value: a.value.trim() })).filter((a) => a.value);
      const out: Record<string, unknown> = {};
      if (row.title_editable && title.trim() && title.trim() !== (row.title ?? "")) out.job = { title: title.trim() };
      if (add.length) out.reach = { add };
      return Object.keys(out).length ? { type: "person", id: row.person_id ?? "", changes: out } : null;
    }
    if (kind === "property") {
      const n = units.trim() === "" ? null : Number(units);
      if (n === (row.unit_count ?? null)) return null;
      return { type: "property", id: row.property_id ?? "", changes: { fields: { unit_count: n } } };
    }
    if (website.trim() === (row.website ?? "")) return null;
    return { type: "company", id: row.company_id ?? "", changes: { fields: { website: website.trim() || null } } };
  };
  const unitsBad = kind === "property" && units.trim() !== "" && !/^\d+$/.test(units.trim());

  const save = useMutation({
    mutationFn: () => {
      const c = changes();
      if (!c) throw new Error("Nothing changed.");
      return callFunction(token, "edit_record", {
        p_entity_type: c.type, p_entity_id: c.id, p_changes: c.changes, p_note: note.trim() || null,
      });
    },
    onSuccess: onSaved,
  });

  const page = kind === "person" ? "person's" : kind === "property" ? "property's" : "company's";
  const recordPath = kind === "person" ? `/people/${row.person_id}` : kind === "property" ? `/properties/${row.property_id}` : `/companies/${row.company_id}`;

  return (
    <form className="row-form er-form" onClick={(e) => e.stopPropagation()}
          onSubmit={(e) => { e.preventDefault(); if (!unitsBad) save.mutate(); }}>
      {kind === "person" ? (
        <>
          {row.title_editable ? (
            <TextInput label="Title" value={title} onChange={(e) => setTitle(e.currentTarget.value)} data-autofocus />
          ) : (
            <span className="muted er-note">
              {row.employment_id ? "This person has more than one job. Change the title on their page."
                : "No job on record. Add the employer on their page."}
            </span>
          )}
          <TextInput label="Add a mobile" value={mobile} onChange={(e) => setMobile(e.currentTarget.value)} inputMode="tel" />
          <TextInput label="Add an email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} inputMode="email" />
          <TextInput label="Add a phone" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} inputMode="tel" />
        </>
      ) : kind === "property" ? (
        <TextInput label="Units" value={units} onChange={(e) => setUnits(e.currentTarget.value)} inputMode="numeric"
                   error={unitsBad ? "A whole number" : undefined} data-autofocus />
      ) : (
        <TextInput label="Website" value={website} onChange={(e) => setWebsite(e.currentTarget.value)} data-autofocus />
      )}
      <TextInput label="Where you found it" value={note} onChange={(e) => setNote(e.currentTarget.value)} className="er-where" />
      <span className="er-buttons">
        <Button kind="orange" size="compact-sm" type="submit" disabled={save.isPending || unitsBad} loading={save.isPending}>Save</Button>
        <Button size="compact-sm" onClick={onCancel} disabled={save.isPending}>Cancel</Button>
        <Link to={recordPath}>Open the {page} page</Link>
      </span>
      {save.isError ? <span className="refusal" role="alert">{(save.error as Error).message}</span> : null}
    </form>
  );
}
